import { useCallback, useMemo, useState } from "react";

import { getDateRangeBounds } from "@/lib/date-range-bounds";

import { getRawValue } from "../../lib/data-entry-format";
import type { CurrentFieldValues, DataRequestFieldResponse, SaveFieldInput } from "../../models/data-request.model";

import { resolveFieldInitialValue } from "./resolve-field-initial-value";
import type { FieldDraft } from "./use-draft-persistence";

type DateFieldKey = "startDate" | "endDate";

const DATE_FIELD_KEYS: readonly DateFieldKey[] = ["startDate", "endDate"];

const isDateFieldKey = (fieldKey: string): fieldKey is DateFieldKey =>
  DATE_FIELD_KEYS.includes(fieldKey as DateFieldKey);

interface UseDateRangeDraftArgs {
  draftMap: Record<string, FieldDraft>;
  fieldResponseMap: Record<string, DataRequestFieldResponse>;
  fieldValues: CurrentFieldValues | null;
}

interface DateRangeDraft {
  maxSelectableStartDate: Date | undefined;
  minSelectableEndDate: Date | undefined;
  recordSave: (input: SaveFieldInput) => void;
}

/**
 * Keeps the two date answers in one place so each picker can bound the other.
 *
 * An answer that clears a date is dropped before the write, so the stored date
 * is still the side the server validates against; a cleared answer therefore
 * falls back to the stored value rather than leaving the other picker
 * unbounded.
 */
export function useDateRangeDraft({
  draftMap,
  fieldResponseMap,
  fieldValues,
}: UseDateRangeDraftArgs): DateRangeDraft {
  const [answeredDate, setAnsweredDate] = useState<Record<DateFieldKey, string | null>>(() =>
    DATE_FIELD_KEYS.reduce(
      (answered, fieldKey) => ({
        ...answered,
        [fieldKey]:
          resolveFieldInitialValue({
            draft: draftMap[fieldKey] ?? null,
            savedResponse: fieldResponseMap[fieldKey] ?? null,
            defaultValue: fieldValues ? getRawValue(fieldValues, fieldKey) : null,
          }) || null,
      }),
      {} as Record<DateFieldKey, string | null>,
    ),
  );

  const recordSave = useCallback((input: SaveFieldInput) => {
    if (!isDateFieldKey(input.fieldKey)) {
      return;
    }
    const nextValue = typeof input.newValue === "string" && input.newValue !== "" ? input.newValue : null;
    setAnsweredDate((current) => ({ ...current, [input.fieldKey as DateFieldKey]: nextValue }));
  }, []);

  const { maxSelectableStartDate, minSelectableEndDate } = useMemo(
    () =>
      getDateRangeBounds({
        startDate: answeredDate.startDate ?? (fieldValues ? getRawValue(fieldValues, "startDate") : null),
        endDate: answeredDate.endDate ?? (fieldValues ? getRawValue(fieldValues, "endDate") : null),
      }),
    [answeredDate, fieldValues],
  );

  return { maxSelectableStartDate, minSelectableEndDate, recordSave };
}
